import { useCallback, useEffect, useRef } from 'react';
import { useRecordCheckoutEventsMutation } from '../store';
import getCheckoutSessionId from '../utils/checkoutSession';
import {
  CheckoutEventInputDTO, CheckoutStep, CheckoutStepProperties, CURRENT_CHECKOUT_VERSION,
} from '../models/checkout-event';

interface CheckoutEventTarget {
  id: string;
  business_id: string;
}

interface TrackOptions {
  orderId?: string | null;
  properties?: CheckoutStepProperties;
}

// El backend acepta hasta 20 pasos por pedido.
const MAX_BATCH = 20;
// Los pasos que pasan juntos (abrir la hoja y elegir) viajan en un solo pedido.
const FLUSH_DELAY_MS = 800;
// El mismo paso dos veces en menos de esto es un remontaje (StrictMode en
// desarrollo, un re-render que remonta la página), no una persona: cuenta uno.
const DUPLICATE_WINDOW_MS = 1000;
const lastTracked = new Map<string, number>();

/**
 * Mide el recorrido de compra paso a paso (checkout_event). `track` no falla
 * nunca ni frena nada: junta los pasos y los manda en lote, y al salir de la
 * página (cerrarla, saltar a la pasarela) manda lo que quede.
 *
 * Sin evento (todavía cargando) o en el backoffice no mide: `enabled` en false.
 */
const useCheckoutEvents = (
  event: CheckoutEventTarget | null | undefined,
  enabled = true,
  // El checkout que ve el comprador: 1 = el actual, 2 = el nuevo.
  version: 1 | 2 = CURRENT_CHECKOUT_VERSION,
) => {
  const [recordCheckoutEvents] = useRecordCheckoutEventsMutation();
  const queue = useRef<CheckoutEventInputDTO[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventRef = useRef(event);
  eventRef.current = event;

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const target = eventRef.current;
    if (!target || queue.current.length === 0) {
      return;
    }
    const pending = queue.current.splice(0, queue.current.length);
    const sessionId = getCheckoutSessionId(target.id);
    for (let start = 0; start < pending.length; start += MAX_BATCH) {
      // Sin await ni unwrap: un error de medición no tiene a quién avisarle.
      recordCheckoutEvents({
        businessId: target.business_id,
        eventId: target.id,
        body: {
          session_id: sessionId,
          checkout_version: version,
          events: pending.slice(start, start + MAX_BATCH),
        },
      });
    }
  }, [recordCheckoutEvents, version]);

  const track = useCallback((step: CheckoutStep, options: TrackOptions = {}) => {
    if (!enabled || !eventRef.current) {
      return;
    }
    const key = `${eventRef.current.id}:${step}:${options.orderId ?? ''}:${JSON.stringify(options.properties ?? null)}`;
    const now = Date.now();
    if (now - (lastTracked.get(key) ?? 0) < DUPLICATE_WINDOW_MS) {
      return;
    }
    lastTracked.set(key, now);
    queue.current.push({
      step,
      order_id: options.orderId ?? null,
      properties: options.properties ?? null,
    });
    if (queue.current.length >= MAX_BATCH) {
      flush();
      return;
    }
    if (!timer.current) {
      timer.current = setTimeout(flush, FLUSH_DELAY_MS);
    }
  }, [enabled, flush]);

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        flush();
      }
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHide);
      // Cambiar de paso desmonta la página: lo pendiente sale ahora.
      flush();
    };
  }, [flush]);

  return { track, flush };
};

export default useCheckoutEvents;
